import React, { PropsWithChildren, useEffect, useState } from "react";
import { ErrorBoundary as ExternalErrorBoundary } from "react-error-boundary";

import { useAppContext } from "../../hooks/useAppContext";
import { useEditor } from "../../hooks/useEditor";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useLabels } from "../../hooks/useLabels";
import { Dialog, DialogButton, DialogContent, DialogFooter, DialogTitle } from "../dialog";
import { Modal } from "../modal";
import { ReactException, useErrorContext } from "./context";
import { labels } from "./labels";

const ErrorUserFeedback = ({
	error,
	componentStack,
	logToServer = false,
	showDetails = false,
}: React.PropsWithChildren<{
	error: any;
	logToServer?: boolean;
	componentStack?: string;
	showDetails?: boolean;
}>) => {
	const [errorId, setErrorId] = useState<string | undefined>(undefined);
	const { createErrorHandler } = useErrorHandling();
	const {
		context: { account },
	} = useAppContext();

	const accountId = account ? account.id : "";

	useEffect(() => {
		if (logToServer) {
			// to maintain error type, we just add a property to the existing error with the componentStack
			if (componentStack) {
				if (typeof error === "object") {
					error.componentStack = componentStack;
				} else {
					// eslint-disable-next-line react-hooks/exhaustive-deps
					error = new ReactException(error, componentStack);
				}
			}

			createErrorHandler({
				onErrorId: (newErrorId) => setErrorId(newErrorId),
			})(error);
		}
	}, [logToServer, error, componentStack]);

	let debugText = `Error Message  : ${eXpress.Exception.getMessages(error).join(", ")}\n`;
	debugText += `Error Type     : ${error?.name ?? "N/A"}\n`;
	if (logToServer) debugText += `Error ID       : ${errorId ?? "logging..."}\n`;
	debugText += `Error Stack    : ${error?.stack ?? "N/A"}\n`;

	const trimmedComponentStack =
		componentStack && componentStack.startsWith("\n")
			? componentStack.slice(1)
			: componentStack;
	if (trimmedComponentStack) debugText += `\nComponent Stack: \n${componentStack}\n`;
	if (accountId) debugText += `Account ID     : ${accountId}\n`;

	// When offline, fetching labels will wait for network connection
	// So we already start UI with the default text
	const [
		[
			titleText = labels.title.default,
			bodyText = labels.message.default,
			refreshText = labels.refresh.default,
			homeText = labels.home.default,
		],
	] = useLabels([labels.title, labels.message, labels.refresh, labels.home]);

	function getErrorBody() {
		let errorBody = bodyText || "";
		errorBody = errorBody.replace("{0}", errorId || "unknown");
		errorBody = errorBody.replace("{1}", accountId || "");
		return errorBody;
	}

	return (
		<Modal isOpen>
			<Dialog className="error-boundary">
				<DialogTitle>{titleText}</DialogTitle>
				<DialogContent>
					{getErrorBody()}
					<pre style={{ userSelect: "text" }}>{showDetails ? debugText : null}</pre>
				</DialogContent>
				<DialogFooter>
					<DialogButton
						onClick={() => {
							const url = window.location.href;
							if (url) window.location.assign(url);
						}}
					>
						{refreshText}
					</DialogButton>

					<DialogButton
						onClick={() => {
							const url = window.location.origin;
							if (url) window.location.assign(url);
						}}
					>
						{homeText}
					</DialogButton>
				</DialogFooter>
			</Dialog>
		</Modal>
	);
};

ErrorUserFeedback.displayName = "ErrorUserFeedback";

// https://github.com/bvaughn/react-error-boundary
export const ErrorBoundary = ({ children }: PropsWithChildren) => {
	const { logger, client } = useEditor();
	const { createErrorHandler } = useErrorHandling();
	const { blockingError, setBlockingError } = useErrorContext();

	// Handle errors
	useEffect(() => {
		function unhandledErrorHandler(e: ErrorEvent) {
			e.preventDefault();
			e.stopPropagation();

			const error =
				e.error ||
				new eXpress.UnhandledJsException(e.message || "No error message", undefined, {
					filename: e.filename || "",
					lineno: e.lineno || 0,
					colno: e.colno || 0,
				});

			// Is 'Script error'? from other domain
			// Remark: all iPhone errors (catched with onerror, even with valid CORS headers) will be converted to 'Script Error'.
			const isScriptError = ((error as Error).message || "").includes("Script error"); // Did not use .includes to support older browsers
			logger.log(`Is blocking error: ${isScriptError ? "No" : "Yes"}`); // For 'Script error' we don't block

			// Log to Server
			// - We currently log all errors, also 'Script Errors'
			createErrorHandler()(error);

			// Show blocking error user feedback
			// - We don't block on 3th-party errors (CORS)
			if (!isScriptError && client.isBlockingError(error, e)) setBlockingError(error);
		}
		window.addEventListener("error", unhandledErrorHandler);
		return () => {
			window.removeEventListener("error", unhandledErrorHandler);
		};
	}, [createErrorHandler, setBlockingError, logger, client]);

	return (
		<>
			{blockingError ? (
				<ErrorUserFeedback
					error={blockingError}
					showDetails={eXpress.isDebugMode}
					logToServer
				/>
			) : (
				<ExternalErrorBoundary
					FallbackComponent={({ error }) => (
						<ErrorUserFeedback
							error={error}
							showDetails={eXpress.isDebugMode}
							logToServer
						/>
					)}
				>
					{children}
				</ExternalErrorBoundary>
			)}
		</>
	);
};
ErrorBoundary.displayName = "ErrorBoundary";
